<template>
    <div v-if="show" id="modal-sam-introduction" class="sam-intro-modal">
        <div class="rp-overlay" @click="closePopup"></div>
        <div class="rp-modal">
            <div class="rp-modal-header">
                <h3 class="rp-modal-title">{{ $t('Smart Labeling Tool') }}</h3>
                <button class="rp-btn-dismiss" @click="closePopup">
                    <XIcon class="w-5 h-5" />
                </button>
            </div>
            <div class="rp-modal-body">
                <div class="sam-intro-content">
                    <div class="sam-intro-video-container">
                        <video ref="videoRef" autoplay loop muted playsinline class="sam-intro-video">
                            <source :src="samVideoSrc" type="video/mp4">
                            {{ $t('Your browser does not support video playback.') }}
                        </video>
                    </div>
                    
                    <div class="sam-intro-text">
                        <h4>{{ $t('How to use Smart Labeling') }}</h4>
                        
                        <div class="sam-intro-steps">
                            <div class="sam-intro-step">
                                <div class="sam-tool-icon">
                                    <div class="toolbar-btn-mock">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                                    </div>
                                </div>
                                <div class="sam-step-text">
                                    <p class="font-medium">{{ $t('Add positive points') }}</p>
                                    <p class="text-sm">{{ $t('Click directly on the object you want to segment. Add multiple points if needed for complex shapes.') }}</p>
                                </div>
                            </div>
                            
                            <div class="sam-intro-step">
                                <div class="sam-tool-icon">
                                    <div class="shortcut-keys">
                                        <div class="key-combo-vertical">
                                            <kbd>Ctrl</kbd>
                                            <span class="plus-sign">+</span>
                                            <kbd>Click</kbd>
                                        </div>
                                    </div>
                                </div>
                                <div class="sam-step-text">
                                    <p class="font-medium">{{ $t('Add negative points') }}</p>
                                    <p class="text-sm">{{ $t('Hold Ctrl and click on areas you want to exclude from the selection. This helps refine the mask.') }}</p>
                                </div>
                            </div>
                            
                            <div class="sam-intro-step">
                                <div class="sam-tool-icon">
                                    <div class="toolbar-btn-mock">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                                    </div>
                                </div>
                                <div class="sam-step-text">
                                    <p class="font-medium">{{ $t('Confirm and apply mask') }}</p>
                                    <p class="text-sm">{{ $t('Click the + button or press Tab to confirm and apply the generated mask to your image.') }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="sam-intro-footer">
                    <div class="checkbox-container">
                        <input type="checkbox" id="dont-show-again" v-model="dontShowAgain">
                        <label for="dont-show-again">{{ $t('Don\'t show this again') }}</label>
                    </div>
                    
                    <div class="rp-modal-body-buttons">
                        <button class="rp-btn-primary" @click="closePopup">
                            {{ $t('Got it!') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import samVideo from '@/assets/videos/sam.mp4';

const props = defineProps({
    show: {
        type: Boolean,
        required: true
    }
});

const emit = defineEmits(['close']);
const SAM_POPUP_PREF_KEY = 'rosepetal-sam-popup-pref';

const dontShowAgain = ref(false);
const videoRef = ref(null);
const samVideoSrc = ref(samVideo);

const closePopup = () => {
    localStorage.setItem(SAM_POPUP_PREF_KEY, dontShowAgain.value);
    emit('close');
};

onMounted(() => {
    const localPreference = localStorage.getItem(SAM_POPUP_PREF_KEY);
    dontShowAgain.value = localPreference === 'true';

    if (videoRef.value && props.show) {
        videoRef.value.load();
    }
});
</script>

<style scoped>
.sam-intro-modal {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 99999;
}

.rp-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(4px);
    z-index: 2000;
}

.rp-modal {
    position: fixed;
    z-index: 2001;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 700px;
    max-width: 95vw;
    max-height: 90vh;
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.rp-modal-header {
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e5e7eb;
    background-color: #f8fafc;
}

.rp-modal-title {
    font-size: 16px;
    font-weight: 500;
    color: var(--primary-600, #1e4591);
    margin: 0;
}

.rp-btn-dismiss {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    color: #64748b;
    transition: all 0.2s;
    background-color: transparent;
    border: none;
    cursor: pointer;
}

.rp-btn-dismiss:hover {
    background-color: #f1f5f9;
    color: #0f172a;
}

.rp-modal-body {
    padding: 20px;
    overflow-y: auto;
}

.sam-intro-content {
    display: flex;
    gap: 30px;
    align-items: center;
}

.sam-intro-video-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 50%;
}

.sam-intro-video {
    width: 100%;
    display: block;
    border-radius: 10px;
    object-fit: contain;
    max-height: 350px;
    background: transparent;
}

.sam-intro-text {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.sam-intro-text h4 {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin-top: 0;
    margin-bottom: 16px;
}

.sam-intro-steps {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.sam-intro-step {
    display: flex;
    gap: 12px;
}

.sam-tool-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
}

.toolbar-btn-mock {
    padding: 8px;
    border-radius: 6px;
    color: var(--primary-600);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background-color: var(--primary-50);
    border: 1px solid var(--primary-200);
}

.shortcut-keys {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 0;
    height: 100%;
}

.key-combo-vertical {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 1.5rem;
    gap: 2px;
}

.plus-sign {
    color: var(--primary-600);
    font-size: 10px;
    line-height: 1;
}

kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    padding: 1px 4px;
    font-size: 11px;
    font-weight: 500;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    color: var(--primary-600);
    background-color: white;
    border: 1px solid var(--primary-200);
    border-radius: 3px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05);
    text-align: center;
}

.sam-step-text {
    flex: 1;
}

.sam-step-text p {
    margin: 0;
    line-height: 1.5;
}

.sam-step-text p:first-child {
    margin-bottom: 4px;
}

.sam-step-text .text-sm {
    font-size: 14px;
    color: #4b5563;
}

.sam-intro-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
}

.checkbox-container {
    display: flex;
    align-items: center;
    gap: 8px;
}

.checkbox-container input[type="checkbox"] {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 1px solid #d1d5db;
    cursor: pointer;
}

.checkbox-container label {
    font-size: 14px;
    color: #4b5563;
    cursor: pointer;
}

.rp-modal-body-buttons {
    display: flex;
    gap: 12px;
}

.rp-btn-primary {
    background-color: var(--primary-600, #1e4591);
    color: white;
    border: none;
    padding: 8px 20px;
    font-size: 14px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.rp-btn-primary:hover {
    background-color: var(--primary-700, #17356e);
}

.rp-btn-secondary {
    background-color: #f3f4f6;
    color: #4b5563;
    border: none;
    padding: 8px 20px;
    font-size: 14px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.rp-btn-secondary:hover {
    background-color: #e5e7eb;
}

.font-medium {
    font-weight: 500;
}

@media (max-width: 768px) {
    .sam-intro-content {
        flex-direction: column;
    }
    
    .rp-modal {
        width: 95vw;
    }
}
</style>
